import { EditOutlined } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Table, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTableContainer from "~/components/GenericTableContainer";
import { dateTimeFormat, roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import useColumnSearch from "~/hooks/useColumnSearch";
import { getPlottingList } from "~/services/plotting.service";
import { getTableParams } from "~/utils/utils";

const itemsPerPage = 5;

const PlottingMaster = () => {
  const navigate = useNavigate();

  const { getColumnSearchProps, setSearchText, setSearchedColumn } =
    useColumnSearch();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    mutate(
      getTableParams({
        pagination: tableParams.pagination,
        filters: selectedKeys[0],
      })
    );
  };

  const columns = [
    {
      title: "Plotting Name",
      dataIndex: "Plot_Name",
      key: "Plot_Name",
      responsive: ["lg", "md"],
      fixed: "left",
      width: 200,

      ...getColumnSearchProps("Plot_Name", handleSearch, "Plot Name"),
    },
    {
      title: "Plotting Code",
      dataIndex: "Plot_Code",
      key: "Plot_Code",
      ...getColumnSearchProps("Plot_Code", handleSearch, "Plot Code"),
      responsive: ["lg", "md"],
      width: 250,
    },
    {
      title: "Location Name",
      dataIndex: "loc_mstr.Loc_Name",
      key: "Loc_Name",

      width: 250,
    },
    {
      title: "Total Plotting Area (Hect.)",
      dataIndex: "Plot_Area",
      key: "Plot_Area",
      responsive: ["lg", "md"],
    },
    {
      title: "Total Number of Planting",
      dataIndex: "Total_Planting_Count",
      key: "Total_Planting_Count",
      responsive: ["lg", "md"],
      width: 150,
    },
    /* new changes - tag v3.0.1 */

    {
      title: "Land Use",
      dataIndex: "Land_Use",
      key: "Land_Use",
      responsive: ["lg", "md"],
    },
    {
      title: "Aspect",
      dataIndex: "Aspect",
      key: "Aspect",
      responsive: ["lg", "md"],
    },
    {
      title: "Soil Texture",
      dataIndex: "Soil_Texture",
      key: "Soil_Texture",
      responsive: ["lg", "md"],
    },
    {
      title: "Presence of Weed or Grass",
      dataIndex: "Presence_of_Weed_or_Grass",
      key: "Presence_of_Weed_or_Grass",
      responsive: ["lg", "md"],
      width: 250,
    },

    {
      title: "Created At",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Created By",
      dataIndex: "created_by.Login_Name",
      key: "created_by.Login_Name",
      responsive: ["lg", "md"],
    },
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Modified By",
      dataIndex: "modified_by.Login_Name",
      key: "modified_by.Login_Name",
      responsive: ["lg", "md"],
    },
    ...(userHasRequiredRole ? [{
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (rowData, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/plotting-master/update/${rowData?.Plot_Name.trim().replace(
                    /\s+/g,
                    "-"
                  )}`,
                  {
                    state: {
                      ...rowData,
                      Loc_Autocomplete_Name: rowData["loc_mstr.Loc_Name"],
                    },
                  }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  const {
    data: plottingData,
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ["plottingData"],
    mutationFn: (payloads) => getPlottingList(payloads),
    onSuccess: (data) => {
      // Modify the data response here

      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: data.count,
          // 200 is mock data, you should read it from server
          // total: data.totalCount,
        },
      });
      console.log("ON SUCCESS", data);
    },
  });
  const handleTableChange = (pagination, filters, sorter) => {
    mutate(getTableParams({ pagination }));

    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  useEffect(() => {
    mutate(getTableParams(tableParams));
  }, []);

  return (
    <GenericTableContainer
      cardHeaderTitle="Plotting Master"
      navigateTo="/plotting-master/create"
      cardButtonText="ADD PLOTTING"
    >
      <Table
        scroll={{ x: 3000, y: 350 }}
        columns={columns}
        dataSource={plottingData?.data}
        loading={isLoading}
        pagination={{
          ...tableParams.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </GenericTableContainer>
  );
};

export default PlottingMaster;
